import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Step {
  number: string;
  title: string;
  description: string;
  benefit?: string;
  details: string;
  icon: string;
}

export default function InteractiveMethodology() {
  const { t } = useTranslation('home');
  const [activeStep, setActiveStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const steps = t('methodology.steps', { returnObjects: true }) as Step[];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, steps.length]);

  const getStepImage = (stepIndex: number) => {
    const imageMap = [
      'https://public.readdy.ai/ai/img_res/c3bdf7c87cffbf2fb19c5314c478eee6.jpg',
      'https://readdy.ai/api/search-image?query=advanced%20financial%20modeling%20and%20analytics%20dashboard%2C%20sophisticated%20data%20visualization%2C%20structural%20analysis%20graphs%2C%20professional%20financial%20technology%20interface&width=800&height=500&seq=method2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=complex%20financial%20scenario%20modeling%2C%20Monte%20Carlo%20simulations%20visualization%2C%20advanced%20analytics%20dashboard%2C%20professional%20risk%20assessment%20tools&width=800&height=500&seq=method3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=professional%20project%20execution%20monitoring%2C%20controlled%20implementation%20process%2C%20sophisticated%20tracking%20dashboard%2C%20executive%20project%20management%20environment&width=800&height=500&seq=method4&orientation=landscape'
    ];
    return imageMap[stepIndex] || imageMap[0];
  };

  const getIconClass = (icon: string) => {
    const iconMap = {
      'search': 'ri-search-line',
      'analytics': 'ri-line-chart-line',
      'calculator': 'ri-calculator-line',
      'checkmark': 'ri-checkbox-circle-line'
    };
    return iconMap[icon as keyof typeof iconMap] || 'ri-circle-line';
  };

  return (
    <section className="py-24 bg-gradient-to-b from-stone-900 to-stone-800 text-white overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full text-sm font-medium mb-4 tracking-wide">
            {t('methodology.eyebrow')}
          </div>
          <h2 className="text-4xl lg:text-5xl font-light mb-6 font-serif">
            {t('methodology.title')}
          </h2>
          <p className="text-xl text-stone-300 max-w-3xl mx-auto leading-relaxed">
            {t('methodology.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Interactive Steps */}
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative cursor-pointer transition-all duration-500 ${
                  index === activeStep ? 'scale-105' : 'hover:scale-102'
                }`}
                onClick={() => {
                  setActiveStep(index);
                  setIsAutoPlaying(false);
                }}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
              >
                <div
                  className={`p-6 rounded-2xl border transition-all duration-500 ${
                    index === activeStep
                      ? 'bg-amber-500/10 border-amber-400/50 shadow-lg shadow-amber-500/20'
                      : 'bg-white/5 border-stone-700 hover:border-stone-600 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Step Number & Icon */}
                    <div className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-500 ${
                      index === activeStep
                        ? 'bg-amber-500 text-white shadow-lg'
                        : 'bg-stone-700 text-stone-300'
                    }`}>
                      <i className={`${getIconClass(step.icon)} text-xl`}></i>
                    </div>

                    <div className="flex-1">
                      {/* Step Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-sm font-mono px-2 py-1 rounded ${
                          index === activeStep
                            ? 'bg-amber-500/20 text-amber-300'
                            : 'bg-stone-700 text-stone-400'
                        }`}>
                          {step.number}
                        </span>
                        <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                          index === activeStep ? 'text-amber-300' : 'text-white'
                        }`}>
                          {step.title}
                        </h3>
                      </div>

                      {/* Step Description */}
                      <p className={`text-sm mb-2 transition-colors duration-300 ${
                        index === activeStep ? 'text-stone-200' : 'text-stone-400'
                      }`}>
                        {step.description}
                      </p>

                      {/* Benefit line — always visible, subtle */}
                      {step.benefit && (
                        <p className={`text-[11px] font-medium transition-all duration-300 mb-3 ${
                          index === activeStep ? 'text-amber-400/80' : 'text-stone-600'
                        }`}>
                          ↳ {step.benefit}
                        </p>
                      )}

                      {/* Step Details - Only show for active step */}
                      <div className={`overflow-hidden transition-all duration-500 ${
                        index === activeStep ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <p className="text-xs text-amber-200 leading-relaxed">
                          {step.details}
                        </p>
                      </div>
                    </div>

                    {/* Progress Indicator */}
                    <div className="flex-shrink-0 w-1 h-16 bg-stone-700 rounded-full overflow-hidden">
                      <div
                        className={`w-full bg-amber-500 transition-all duration-500 ${
                          index === activeStep ? 'h-full' : 'h-0'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Connecting Line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-8 top-full w-0.5 h-6 bg-gradient-to-b from-stone-600 to-transparent z-0" />
                )}
              </div>
            ))}

            {/* Auto-play Controls */}
            <div className="flex items-center gap-4 pt-6">
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                  isAutoPlaying
                    ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                    : 'bg-stone-700 text-stone-300 hover:bg-stone-600'
                }`}
              >
                <i className={`${isAutoPlaying ? 'ri-pause-line' : 'ri-play-line'}`}></i>
                {isAutoPlaying ? 'Pausar' : 'Reproduzir'}
              </button>

              <div className="flex gap-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setActiveStep(index);
                      setIsAutoPlaying(false);
                    }}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === activeStep ? 'bg-amber-500 w-6' : 'bg-stone-600 hover:bg-stone-500'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Dynamic Image Display */}
          <div className="relative">
            <div className="relative h-96 lg:h-[500px] rounded-2xl overflow-hidden bg-stone-800">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ${
                    index === activeStep
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={getStepImage(index)}
                    alt={step.title}
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent" />
                  
                  {/* Step Info Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/40 backdrop-blur-sm p-4 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-amber-500 text-white px-2 py-1 rounded text-xs font-mono">
                          {step.number}
                        </span>
                        <h4 className="font-semibold text-white">{step.title}</h4>
                      </div>
                      <p className="text-stone-200 text-sm">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl" />
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-12 max-w-md mx-auto">
          <div className="flex justify-between text-sm text-stone-400 mb-2">
            <span>Progresso da Metodologia</span>
            <span>{Math.round(((activeStep + 1) / steps.length) * 100)}%</span>
          </div>
          <div className="w-full bg-stone-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-amber-500 to-amber-400 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}